/**
 * StretchResolutionPanel — Shows results after a stretch resolves.
 * AI narrative + mechanical results + next stretch button.
 */

import { ArrowRight, Swords, MapPin, Package, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StretchResolution } from '@omen-quest/stretch-engine';

interface StretchResolutionPanelProps {
  resolution: StretchResolution;
  narrative?: string;
  onNextStretch: () => void;
  isEnded?: boolean;
  className?: string;
}

export function StretchResolutionPanel({
  resolution,
  narrative,
  onNextStretch,
  isEnded = false,
  className,
}: StretchResolutionPanelProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* AI Narrative */}
      {narrative && (
        <div className="bg-neutral-900/60 rounded-lg p-4 border border-white/5">
          <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap">
            {narrative}
          </p>
        </div>
      )}

      {/* Activity results */}
      <div className="space-y-2">
        {resolution.activityResults.map(result => (
          <div key={result.playerId} className="bg-neutral-900/40 rounded-lg px-3 py-2 border border-white/5">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-3.5 h-3.5 text-neutral-500" />
              <span className="text-xs font-medium text-neutral-300 capitalize">
                {result.activityType.replace('_', ' ')}
              </span>
            </div>
            {result.notes.map((note, i) => (
              <p key={i} className="text-xs text-neutral-400 ml-5">{note}</p>
            ))}
            {result.itemsFound.length > 0 && (
              <div className="flex items-center gap-1 ml-5 mt-1">
                <Package className="w-3 h-3 text-amber-400" />
                <span className="text-xs text-amber-400">
                  Found {result.itemsFound.length} item{result.itemsFound.length > 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Wandering monster result */}
      {resolution.wanderingMonster.checked && (
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-xs border',
          resolution.wanderingMonster.triggered
            ? 'bg-red-950/30 border-red-800/30 text-red-400'
            : 'bg-neutral-800/30 border-white/5 text-neutral-500',
        )}>
          <Swords className="w-3.5 h-3.5" />
          {resolution.wanderingMonster.triggered
            ? `Encounter! ${resolution.wanderingMonster.encounter?.description ?? 'Something approaches...'}`
            : `Wandering monster check: safe (rolled ${resolution.wanderingMonster.rolled})`
          }
        </div>
      )}

      {/* Timeline events */}
      {resolution.timelineEventsFired.length > 0 && (
        <div className="space-y-1">
          {resolution.timelineEventsFired.map(event => (
            <div key={event.id} className="flex items-center gap-2 px-3 py-1.5 rounded bg-purple-950/20 border border-purple-800/20 text-xs text-purple-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>{event.type}: {JSON.stringify(event.data).slice(0, 80)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Depleted lights */}
      {resolution.lightSourcesDepleted.length > 0 && (
        <div className="text-xs text-amber-400/80 bg-amber-950/20 rounded px-3 py-1.5 border border-amber-800/20">
          Light source depleted: {resolution.lightSourcesDepleted.map(s => s.type).join(', ')}
        </div>
      )}

      {/* Next stretch / ended */}
      {!isEnded ? (
        <button
          onClick={onNextStretch}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white/10 text-white hover:bg-white/15 border border-white/20 text-sm font-medium transition-colors"
        >
          Next Stretch
          <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <div className="text-center text-sm text-neutral-400 py-2">
          Exploration ended.
        </div>
      )}
    </div>
  );
}
